import { PROMO_KIND_CODE, PROMO_KIND_DATE, normaliseCode } from './promoTypes';

/**
 * Pure resolver: given the full promo list, the current time, and any
 * code the visitor brought with them (URL or manual entry), pick the
 * promo that should apply right now.
 *
 * Rules:
 *   - Inactive promos never match.
 *   - Code promos match only if their code === provided code AND the
 *     current time is within [startsAt, endsAt] (when set).
 *   - Date promos match only if the current time is within
 *     [startsAt, endsAt]. They ignore the code entirely.
 *   - When multiple promos match, the one with the highest
 *     discountPercent wins. Ties prefer code promos so that influencer
 *     attribution is preserved (their `tc` value flows through).
 *   - Returns null when nothing matches.
 *
 * `enteredCode` takes precedence over `urlCode` when both are set —
 * a manual entry is always a deliberate user action.
 */
export function resolveActivePromo({ promos, now, urlCode, enteredCode }) {
    if (!Array.isArray(promos) || promos.length === 0) return null;

    const code = normaliseCode(enteredCode) || normaliseCode(urlCode);
    const nowMs = now instanceof Date ? now.getTime() : Number(now);

    const matches = promos.filter((p) => {
        if (!p || p.active === false) return false;
        if (!withinWindow(p, nowMs)) return false;

        if (p.kind === PROMO_KIND_CODE) {
            return Boolean(code) && normaliseCode(p.code) === code;
        }
        if (p.kind === PROMO_KIND_DATE) {
            // Date promos require both ends of the window to be set —
            // an open-ended date promo is almost certainly a config bug.
            return Boolean(p.startsAt && p.endsAt);
        }
        return false;
    });

    if (matches.length === 0) return null;

    matches.sort((a, b) => {
        const dp = (b.discountPercent || 0) - (a.discountPercent || 0);
        if (dp !== 0) return dp;
        // Tie-break: code promos win so influencer `tc` attribution stays.
        if (a.kind === PROMO_KIND_CODE && b.kind !== PROMO_KIND_CODE) return -1;
        if (b.kind === PROMO_KIND_CODE && a.kind !== PROMO_KIND_CODE) return 1;
        return 0;
    });

    return matches[0];
}

function withinWindow(promo, nowMs) {
    const startMs = toMillis(promo.startsAt);
    const endMs = toMillis(promo.endsAt);
    if (startMs !== null && nowMs < startMs) return false;
    if (endMs !== null && nowMs > endMs) return false;
    return true;
}

/**
 * Accept Firestore Timestamp, JS Date, ISO string, or millis. Returns
 * null when the value is missing so the window check treats it as
 * "no boundary on this end".
 */
function toMillis(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
}
